// Where a settings screen is (Settings, a space's, a library's), kept in the URL under its own
// key (?settings=menu, ?space-settings=roles, …) so the phone's Back and Android's back
// gesture step out one level, and other screens can open a section directly.
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

/** Per screen: the history position just before it opened, to leave it in one step. */
const before = new Map<string, number | null>()
const position = (): number | null => (typeof history.state?.position === 'number' ? history.state.position : null)

export function useSectionNav<S extends string>(key: string) {
  const route = useRoute()
  const router = useRouter()

  /** 'menu', a section, or null while the screen is closed. */
  const at = computed(() => {
    const v = route.query[key]
    return typeof v === 'string' ? v : null
  })

  function go(where: string | null, replace = false): void {
    const query = { ...route.query }
    if (where) query[key] = where
    else delete query[key]
    void (replace ? router.replace({ query }) : router.push({ query }))
  }

  /** Opens the screen on its menu, or straight on a section. */
  function open(section?: S): void {
    if (!at.value) before.set(key, position())
    go(section ?? 'menu')
  }

  /** From the menu into a section. */
  const enter = (section: S) => go(section)

  /** From a section back to the menu: a history step if we came from it, else in place. */
  function back(): void {
    const b = before.get(key) ?? null
    const p = position()
    if (b !== null && p !== null && p > b + 1) router.back()
    else go('menu', true)
  }

  /** Leaves the screen, removing its history entries (a deep link is just replaced). */
  function close(): void {
    const b = before.get(key) ?? null
    const p = position()
    if (b !== null && p !== null && p > b) router.go(b - p)
    else go(null, true)
    before.delete(key)
  }

  return { at, open, enter, back, close }
}
